/**
 * Validation shared by the site's forms and the functions that save them.
 * Pure: no imports from the app, so netlify/functions can use it too.
 */

export const EMAIL_MAX = 254

export function normalizeEmail(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
}

export function isEmail(value: string): boolean {
  return value.length <= EMAIL_MAX && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)
}

function line(raw: unknown, max: number): string {
  return typeof raw === 'string' ? raw.trim().replace(/\s+/g, ' ').slice(0, max + 1) : ''
}

export interface ContactInput {
  firstName: string
  lastName: string
  email: string
  subject: string
  message: string
}

export type Validation<T> = { ok: true; value: T } | { ok: false; errors: Partial<Record<keyof T, string>> }

export const MESSAGE_MAX = 5000

export function validateContact(input: Record<string, unknown>): Validation<ContactInput> {
  const value: ContactInput = {
    firstName: line(input.firstName, 80),
    lastName: line(input.lastName, 80),
    email: normalizeEmail(input.email),
    subject: line(input.subject, 200),
    // Keep line breaks in the message body.
    message: typeof input.message === 'string' ? input.message.trim().slice(0, MESSAGE_MAX + 1) : '',
  }
  const errors: Partial<Record<keyof ContactInput, string>> = {}
  if (!value.firstName) errors.firstName = 'Please enter your first name.'
  else if (value.firstName.length > 80) errors.firstName = 'That is too long.'
  if (!value.lastName) errors.lastName = 'Please enter your last name.'
  else if (value.lastName.length > 80) errors.lastName = 'That is too long.'
  if (!isEmail(value.email)) errors.email = 'Please enter a valid email address.'
  if (!value.subject) errors.subject = 'Please enter a subject.'
  else if (value.subject.length > 200) errors.subject = 'That is too long.'
  if (value.message.length < 2) errors.message = 'Please enter a message.'
  else if (value.message.length > MESSAGE_MAX) errors.message = `Please keep your message under ${MESSAGE_MAX.toLocaleString('en-US')} characters.`
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value }
}

export function validateNewsletter(input: Record<string, unknown>): Validation<{ email: string }> {
  const email = normalizeEmail(input.email)
  if (!isEmail(email)) return { ok: false, errors: { email: 'Please enter a valid email address.' } }
  return { ok: true, value: { email } }
}
